import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import InsightsWidget from "../../components/InsightsWidget";
import { Workflow as WorkflowIcon, Plus, TrendingUp, Zap, Clock, Euro, Sparkles, ArrowRight, MessageCircle } from "lucide-react";

export default function Dashboard() {
  const { user } = useAuth();
  const [analytics, setAnalytics] = useState(null);
  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/analytics/overview").then((r) => r.data),
      api.get("/workflows").then((r) => r.data),
    ]).then(([a, w]) => {
      setAnalytics(a);
      setWorkflows(w);
    }).finally(() => setLoading(false));
  }, []);

  const stats = [
    { label: "Workflows actifs", value: analytics?.active_workflows ?? 0, icon: Zap },
    { label: "Exécutions totales", value: analytics?.total_executions ?? 0, icon: WorkflowIcon },
    { label: "Heures économisées", value: analytics?.hours_saved ?? 0, icon: Clock, suffix: "h" },
    { label: "ROI estimé", value: analytics?.roi_euros ?? 0, icon: Euro, prefix: "€" },
  ];

  return (
    <div className="p-8 max-w-7xl mx-auto" data-testid="dashboard-page">
      {/* Trial banner */}
      {user?.subscription_status === "trial" && user?.trial_ends_at && (() => {
        const daysLeft = Math.max(0, Math.ceil((new Date(user.trial_ends_at) - new Date()) / 86400000));
        return (
          <div className="bg-amber-50 border border-amber-200 rounded-sm px-5 py-3 mb-8 flex items-center justify-between" data-testid="trial-banner">
            <div className="flex items-center gap-3">
              <Sparkles size={16} className="text-amber-600"/>
              <p className="text-sm">
                <strong>Essai gratuit</strong> — plus que <strong>{daysLeft} jour{daysLeft > 1 ? "s" : ""}</strong> pour explorer toutes les fonctionnalités.
              </p>
            </div>
            <Link to="/#pricing">
              <Button className="rounded-sm bg-amber-600 text-white hover:bg-amber-700 h-8 text-xs" data-testid="trial-upgrade-btn">
                Choisir un plan →
              </Button>
            </Link>
          </div>
        );
      })()}

      <div className="flex items-start justify-between mb-10">
        <div>
          <span className="overline">Tableau de bord</span>
          <h1 className="font-display text-4xl font-medium tracking-tighter mt-2">
            Bonjour, {user?.full_name?.split(" ")[0]}.
          </h1>
          <p className="text-zinc-600 mt-2">Vue d'ensemble de vos automatisations.</p>
        </div>
        <Link to="/app/workflows/new">
          <Button className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="dashboard-new-workflow-btn">
            <Plus size={16} className="mr-2"/> Nouveau workflow
          </Button>
        </Link>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-10">
        {stats.map((s, idx) => {
          const Icon = s.icon;
          return (
            <motion.div
              key={s.label}
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.08, duration: 0.4 }}
              whileHover={{ y: -2 }}
              className={`rounded-sm p-6 border transition-all ${idx === 3 ? "bg-black text-white border-black" : "bg-white border-zinc-200"}`}
              data-testid={`kpi-card-${idx}`}
            >
              <div className="flex items-center justify-between mb-4">
                <Icon size={18} strokeWidth={1.5} className={idx === 3 ? "text-white" : "text-zinc-500"}/>
                <span className={`overline ${idx === 3 ? "text-zinc-400" : ""}`}>{s.label}</span>
              </div>
              <p className="font-display text-4xl font-semibold tracking-tighter">
                {s.prefix}{loading ? "—" : s.value}{s.suffix}
              </p>
            </motion.div>
          );
        })}
      </div>

      <InsightsWidget />

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10">
        <Link to="/app/whatsapp" data-testid="quick-action-whatsapp">
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-emerald-600 text-white rounded-sm p-6 relative overflow-hidden h-full"
          >
            <MessageCircle size={20} className="mb-3"/>
            <h3 className="font-display text-xl font-medium tracking-tight mb-1">Pilotez depuis WhatsApp</h3>
            <p className="text-sm text-emerald-100">Commandes vocales, alertes, ROI mensuel.</p>
            <Badge className="absolute top-6 right-6 bg-white text-emerald-700 rounded-sm font-mono text-[10px]">NEW</Badge>
          </motion.div>
        </Link>
        <Link to="/app/workflows/new" data-testid="quick-action-ai">
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-black text-white rounded-sm p-6 grain relative overflow-hidden h-full"
          >
            <Sparkles size={20} className="mb-3"/>
            <h3 className="font-display text-xl font-medium tracking-tight mb-1">Décris, l'IA construit</h3>
            <p className="text-sm text-zinc-400">Génère un workflow complet en langage naturel.</p>
            <ArrowRight size={16} className="absolute top-6 right-6"/>
          </motion.div>
        </Link>
        <Link to="/app/marketplace">
          <motion.div
            whileHover={{ y: -2 }}
            className="bg-white border border-zinc-200 rounded-sm p-6 h-full relative"
            data-testid="quick-action-templates"
          >
            <WorkflowIcon size={20} className="mb-3 klein-text"/>
            <h3 className="font-display text-xl font-medium tracking-tight mb-1">17 templates métier</h3>
            <p className="text-sm text-zinc-600">Comptable, marketing, e-commerce, RH, commercial.</p>
            <ArrowRight size={16} className="absolute top-6 right-6 text-zinc-400"/>
          </motion.div>
        </Link>
      </div>

      {/* Recent workflows */}
      <div className="bg-white border border-zinc-200 rounded-sm" data-testid="recent-workflows-section">
        <div className="p-6 border-b border-zinc-200 flex items-center justify-between">
          <h2 className="font-display text-xl font-medium tracking-tight">Vos workflows</h2>
          <Link to="/app/workflows" className="text-sm text-zinc-600 hover:text-black" data-testid="see-all-workflows">
            Voir tous →
          </Link>
        </div>
        {loading ? (
          <div className="p-12 text-center text-sm text-zinc-500">Chargement...</div>
        ) : workflows.length === 0 ? (
          <div className="p-12 text-center">
            <WorkflowIcon size={40} strokeWidth={1.5} className="mx-auto text-zinc-300 mb-4"/>
            <p className="font-display text-xl font-medium tracking-tight mb-2">Aucun workflow pour l'instant.</p>
            <p className="text-sm text-zinc-500 mb-6">Décrivez un processus à automatiser, l'IA s'occupe du reste.</p>
            <Link to="/app/workflows/new">
              <Button className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="empty-create-workflow">
                <Plus size={16} className="mr-2"/> Créer un workflow
              </Button>
            </Link>
          </div>
        ) : (
          <ul className="divide-y divide-zinc-200">
            {workflows.slice(0, 5).map((w) => (
              <li key={w.id}>
                <Link
                  to={`/app/workflows/${w.id}`}
                  className="flex items-center justify-between p-5 hover:bg-zinc-50 transition-colors"
                  data-testid={`workflow-row-${w.id}`}
                >
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="w-10 h-10 rounded-sm bg-zinc-100 flex items-center justify-center shrink-0">
                      <WorkflowIcon size={18} strokeWidth={1.5}/>
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{w.name}</p>
                      <p className="text-xs text-zinc-500 truncate">
                        {w.steps?.length || 0} étape{(w.steps?.length || 0) > 1 ? "s" : ""} · {w.execution_count || 0} exécutions
                      </p>
                    </div>
                  </div>
                  <Badge className={w.active ? "klein-bg text-white rounded-sm font-mono text-[10px]" : "bg-zinc-200 text-zinc-700 rounded-sm font-mono text-[10px]"}>
                    {w.active ? "ACTIF" : "BROUILLON"}
                  </Badge>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
